import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

function decodeJwtIat(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.iat || null;
  } catch {
    return null;
  }
}

export default function Overview() {
  const { name, email, token } = useAuth();
  const [postCount, setPostCount] = useState(null);
  const [boardCount, setBoardCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const [postsRes, boardsRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/posts/all`, { headers: { ...authHeaders, 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE}/api/boards`),
      ]);
      if (cancelled) return;
      if (postsRes.status === 'fulfilled' && postsRes.value.ok) {
        const posts = await postsRes.value.json().catch(() => []);
        setPostCount(Array.isArray(posts) ? posts.length : null);
      } else {
        setPostCount(null);
      }
      if (boardsRes.status === 'fulfilled' && boardsRes.value.ok) {
        const boards = await boardsRes.value.json().catch(() => []);
        setBoardCount(Array.isArray(boards) ? boards.length : null);
      } else {
        setBoardCount(null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const iat = token ? decodeJwtIat(token) : null;
  const lastSignedIn = iat
    ? new Date(iat * 1000).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : null;

  return (
    <div className="sa-overview-page">
      {/* Decorative backdrop — theme-coloured mountain-range silhouette, perspective
          grid and horizon line, echoing the hero backdrop on the public site.
          Purely a background layer behind the hero + stat cards. */}
      <div className="dg-chat-backdrop" aria-hidden>
        <i className="dg-spotlight" /><i className="dg-grid-wall" />
        <svg className="dg-waves" viewBox="30 126 180 70" preserveAspectRatio="none" aria-hidden focusable="false">
          <defs>
            <linearGradient id="saWvBack" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style={{ stopColor: 'var(--green)' }} /><stop offset="1" style={{ stopColor: 'var(--accent-rose)' }} /></linearGradient>
            <linearGradient id="saWvFront" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style={{ stopColor: 'var(--accent-rose)' }} /><stop offset="1" style={{ stopColor: 'var(--blue)' }} /></linearGradient>
            <linearGradient id="saWvDeep" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style={{ stopColor: 'var(--blue)' }} /><stop offset="1" style={{ stopColor: 'color-mix(in srgb, var(--blue) 40%, var(--bg))' }} /></linearGradient>
          </defs>
          <path d="M30,196 L30,150 C55,132 78,150 100,140 C128,127 155,150 180,138 C194,131 205,140 210,134 L210,196 Z" fill="url(#saWvBack)" />
          <path d="M30,196 L30,172 C60,158 92,178 122,166 C150,155 178,175 210,160 L210,196 Z" fill="url(#saWvFront)" />
          <path d="M30,196 L30,186 C80,180 150,192 210,182 L210,196 Z" fill="url(#saWvDeep)" />
          <path d="M40,178 C90,168 150,184 205,172" style={{ stroke: 'var(--accent-gold)' }} strokeOpacity="0.22" strokeWidth="1.4" fill="none" />
        </svg>
        <i className="dg-horizon" /><i className="dg-grid-floor" />
      </div>

      <div className="sa-overview-content">
        <div className="sa-overview-hero">
          <span className="section-label">Admin</span>
          <h1 className="sa-overview-title">Welcome back{name ? `, ${name.split(' ')[0]}` : ''}.</h1>
          <p className="sa-overview-desc">
            This is the control room for parthmerchant.com — manage the blog, the media
            grid, the homepage copy and the site theme from here.
          </p>
        </div>

        <div className="sa-stat-grid">
          <div className="sa-stat-card">
            <span className="sa-stat-label">Blog Posts</span>
            <span className="sa-stat-value">{loading ? '—' : postCount ?? '—'}</span>
            <span className="sa-stat-sub">Total posts, published + draft</span>
          </div>
          <div className="sa-stat-card">
            <span className="sa-stat-label">Grid Boards</span>
            <span className="sa-stat-value">{loading ? '—' : boardCount ?? '—'}</span>
            <span className="sa-stat-sub">Boards on the media grid</span>
          </div>
          <div className="sa-stat-card">
            <span className="sa-stat-label">Signed In As</span>
            <span className="sa-stat-value sa-stat-value--small">{email || '—'}</span>
            <span className="sa-stat-sub">{lastSignedIn ? `Last sign-in ${lastSignedIn}` : 'Session active'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
